// „Разнасяне на отчети" — ОПЕРАЦИЯТА (кутия 1 на таблото „Склад"): вляво
// чакащите отчети (най-старият отгоре, той е избран по подразбиране; под
// избраната карта — разбивката по сервитьори и станции), вдясно разнасянето на
// избрания. Под 1024px списъкът е лента над детайла и разбивката е сгъваем ред
// в самия детайл. Разнесените документи живеят в справката „Разнесени отчети"
// (`posted-reports`).
//
// Router state: `{selectReportId, prefillSubstitutions}` — „Коригирай и
// разнеси повторно" от справката отваря отчета наново с досегашните замени.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider, Spin } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import {
  useInterchangeGroups,
  usePendingReports,
} from "../../../hooks/useInventory";
import { usePendingPreviews } from "../../../hooks/useInventoryPostings";
import type {
  PendingReport,
  ReportPosting,
  ReportSubstitutionItem,
} from "../../../api/inventory";
import { useIsDarkTheme } from "../../../store/hooks";
import { PageHeader } from "../components/list/PageHeader";
import { EmptyScreen } from "../components/list/ListBits";
import { SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { useActionToast } from "../components/useActionToast";
import { useInventoryShell } from "../components/shell/InventoryShellContext";
import { useLeaveConfirm } from "../components/confirm/useLeaveConfirm";
import { PendingReportList } from "./components/PendingReportList";
import { PendingReportBreakdown } from "./components/PendingReportBreakdown";
import { ReportPostingDetail } from "./components/ReportPostingDetail";
import {
  pendingStatusFromPreview,
  pendingStatusParts,
  type PostingStatusPart,
} from "./components/pendingReportStatus";

// Статусът в списъка се смята от прегледа на всеки отчет; при много чакащи
// зареждаме само първите (най-старите) — останалите показват само мета.
const STATUS_PREVIEW_LIMIT = 12;

type PostingRouterState = {
  selectReportId?: number;
  prefillSubstitutions?: ReportSubstitutionItem[];
} | null;

export const ReportPostingPage = () => {
  const { t } = useTranslation();
  const isDark = useIsDarkTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useViewport();
  const { registerLeaveGuard } = useInventoryShell();
  const showToast = useActionToast();

  const { data: pendingRaw = [], isLoading } = usePendingReports();
  const pending = useMemo(
    () =>
      [...pendingRaw].sort(
        (a, b) =>
          a.reportDate.localeCompare(b.reportDate) || a.reportNumber - b.reportNumber,
      ),
    [pendingRaw],
  );

  // Избраният отчет: от router state, иначе най-старият
  const routerState = location.state as PostingRouterState;
  const [selectedId, setSelectedId] = useState<number | null>(
    routerState?.selectReportId ?? null,
  );
  const [prefill, setPrefill] = useState<{
    reportId: number;
    substitutions: ReportSubstitutionItem[];
  } | null>(
    routerState?.selectReportId && routerState.prefillSubstitutions
      ? {
          reportId: routerState.selectReportId,
          substitutions: routerState.prefillSubstitutions,
        }
      : null,
  );
  useEffect(() => {
    if (!routerState) return;
    // state-ът се чисти, за да не се прилага пак при refresh/назад
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selected =
    pending.find((report) => report.id === selectedId) ?? pending[0] ?? null;

  // ─── Пазач: незапазените замени на избрания отчет ─────────────────────────
  // Въпросът назовава отчета (components/confirm/useLeaveConfirm); номерът се
  // чете през ref, за да не сменя пазачът идентичността си при всеки избор.
  const dirtyRef = useRef(false);
  const leaveAskingRef = useRef(false);
  const selectedNumberRef = useRef<number | null>(null);
  selectedNumberRef.current = selected?.reportNumber ?? null;
  const confirmLeave = useLeaveConfirm();
  const requestLeave = useCallback(
    (proceed: () => void) => {
      if (!dirtyRef.current) {
        proceed();
        return;
      }
      if (leaveAskingRef.current) return;
      leaveAskingRef.current = true;
      const number = selectedNumberRef.current ?? "";
      void confirmLeave({
        document: t("inventoryPage.cfmDocPosting", { number }),
        explanation: t("inventoryPage.cfmLeavePostingText", { number }),
      }).then((confirmed) => {
        leaveAskingRef.current = false;
        if (!confirmed) return;
        dirtyRef.current = false;
        proceed();
      });
    },
    [confirmLeave, t],
  );
  useEffect(() => {
    registerLeaveGuard(requestLeave);
    return () => registerLeaveGuard(null);
  }, [registerLeaveGuard, requestLeave]);

  const selectReport = (reportId: number) => {
    if (reportId === selected?.id) return;
    requestLeave(() => {
      setSelectedId(reportId);
      setPrefill(null);
    });
  };

  // ─── Статусът на всеки чакащ отчет ───────────────────────────────────────
  const { data: groups = [] } = useInterchangeGroups();
  const groupMemberIds = useMemo(
    () => new Set(groups.flatMap((group) => group.members.map((m) => m.productId))),
    [groups],
  );
  const statusIds = pending.slice(0, STATUS_PREVIEW_LIMIT).map((report) => report.id);
  const previews = usePendingPreviews(statusIds);
  const statusOf = (report: PendingReport): PostingStatusPart[] | null => {
    if (report.annulledPostingId != null)
      return [
        {
          tone: "neutral",
          text: t("inventoryPage.postingStatusReturned", { number: report.annulledPostingId }),
        },
      ];
    const index = statusIds.indexOf(report.id);
    const preview = index >= 0 ? previews[index]?.data : undefined;
    if (!preview) return null;
    return pendingStatusParts(pendingStatusFromPreview(preview, groupMemberIds), t);
  };

  const onPosted = (saved: ReportPosting, report: PendingReport) => {
    setPrefill(null);
    setSelectedId(null); // следващият най-стар става избран
    showToast({
      text: t("inventoryPage.postingPostedToast", { number: report.reportNumber }),
      actionLabel: t("inventoryPage.postingViewDocument"),
      onAction: () =>
        navigate("/inventory/posted-reports", { state: { openDoc: saved } }),
    });
  };

  const goPosted = () => requestLeave(() => navigate("/inventory/posted-reports"));

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#D97706",
          colorPrimaryHover: "#B45309",
          controlItemBgActive: isDark ? "#382a12" : "#FFF4E0",
          borderRadius: 8,
        },
      }}
    >
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.posting")}
          subtitle={
            pending.length
              ? t("inventoryPage.postingPendingSubtitle", { count: pending.length })
              : t("inventoryPage.postingNoPending")
          }
          right={
            <button type="button" onClick={goPosted} className={SECONDARY_BUTTON_CLASS}>
              {t("inventoryPage.postingGoPosted")}
            </button>
          }
        />
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : !selected ? (
          <EmptyScreen
            Icon={CheckOutlined}
            title={t("inventoryPage.postingAllPostedTitle")}
            text={t("inventoryPage.postingAllPostedText")}
            action={
              <button type="button" onClick={goPosted} className={SECONDARY_BUTTON_CLASS}>
                {t("inventoryPage.postingViewPosted")}
              </button>
            }
          />
        ) : (
          <div className={`flex min-h-0 flex-1 ${isDesktop ? "flex-row" : "flex-col"}`}>
            <PendingReportList
              reports={pending}
              selectedId={selected.id}
              onSelect={selectReport}
              statusOf={statusOf}
              orientation={isDesktop ? "vertical" : "horizontal"}
              selectedExtra={
                isDesktop ? (
                  <PendingReportBreakdown reportId={selected.id} variant="card" />
                ) : null
              }
            />
            <ReportPostingDetail
              key={selected.id}
              report={selected}
              breakdownInline={!isDesktop}
              prefillSubstitutions={
                prefill?.reportId === selected.id ? prefill.substitutions : undefined
              }
              onPosted={onPosted}
              onDirtyChange={(dirty) => {
                dirtyRef.current = dirty;
              }}
              onLeaveModule={() => requestLeave(() => navigate("/"))}
            />
          </div>
        )}
      </div>
    </ConfigProvider>
  );
};

export default ReportPostingPage;
